import {ErrorEnvelope,PublicCatalogPage,PublicCatalogQuery,PUBLIC_CATALOG_LIMITS,type PublicCatalogPageData} from '@zzy/contracts';
import {CatalogReadError,type PublicCatalogReader} from './api-platform';

const endpoint='/api/v1/catalog/public';
const invalid=()=>new CatalogReadError('invalid_response');

async function readJson(response:Response,signal:AbortSignal):Promise<unknown>{
  const length=response.headers.get('content-length');
  if(!/^application\/json(?:\s*;|$)/i.test(response.headers.get('content-type')??'')||
    length!==null&&(!/^\d+$/.test(length)||BigInt(length)>BigInt(PUBLIC_CATALOG_LIMITS.maxResponseBytes))){
    await response.body?.cancel().catch(()=>{});throw invalid();
  }
  const reader=response.body?.getReader();if(!reader)throw invalid();
  const chunks:Uint8Array[]=[];let bytes=0;
  const cancel=()=>{void reader.cancel().catch(()=>{})};
  signal.addEventListener('abort',cancel,{once:true});
  try{
    for(;;){
      signal.throwIfAborted();const part=await reader.read();signal.throwIfAborted();
      if(part.done)break;
      bytes+=part.value.byteLength;if(bytes>PUBLIC_CATALOG_LIMITS.maxResponseBytes)throw invalid();
      chunks.push(part.value);
    }
    const body=new Uint8Array(bytes);let offset=0;
    for(const chunk of chunks){body.set(chunk,offset);offset+=chunk.byteLength}
    return JSON.parse(new TextDecoder('utf-8',{fatal:true}).decode(body));
  }catch(error){if(error instanceof CatalogReadError||signal.aborted)throw error;throw invalid()}
  finally{signal.removeEventListener('abort',cancel);await reader.cancel().catch(()=>{});reader.releaseLock()}
}

// Same-origin anonymous GET only. The existing read session owns the single
// eight-second budget and AbortSignal; this adapter never retries or seeds data.
export function createPublicCatalogReader(fetcher:typeof fetch):PublicCatalogReader{
  return async(input,signal):Promise<PublicCatalogPageData>=>{
    const query=PublicCatalogQuery.parse(input),params=new URLSearchParams();
    for(const key of ['search','category','vendorId','groupId','sort','limit','cursor'] as const){
      if(query[key]!==undefined)params.set(key,String(query[key]));
    }
    signal.throwIfAborted();
    let response:Response;
    try{response=await fetcher(endpoint+'?'+params.toString(),{
      method:'GET',headers:{accept:'application/json'},credentials:'omit',
      redirect:'error',cache:'no-store',signal,
    })}catch{throw new CatalogReadError('read_failed')}
    if(response.redirected||response.status>=300&&response.status<400){await response.body?.cancel().catch(()=>{});throw invalid()}
    const body=await readJson(response,signal);
    if(response.status!==200){
      const envelope=ErrorEnvelope.safeParse(body);if(!envelope.success)throw invalid();
      const code=envelope.data.error.code;
      if(response.status===409||response.status===410)throw new CatalogReadError('snapshot_changed');
      if(code==='CATALOG_TIMEOUT')throw new CatalogReadError('timeout');
      if(response.status===403||response.status===404||code==='CATALOG_SOURCE_DISABLED'||code==='CATALOG_PUBLIC_CLOSED')throw new CatalogReadError('unavailable');
      throw new CatalogReadError('read_failed');
    }
    if(!body||typeof body!=='object'||Array.isArray(body)||
      Object.keys(body).some(key=>key!=='data'&&key!=='request_id'))throw invalid();
    const envelope=body as Record<string,unknown>;
    if(!ErrorEnvelope.shape.error.shape.request_id.safeParse(envelope.request_id).success)throw invalid();
    const page=PublicCatalogPage.safeParse(envelope.data);if(!page.success)throw invalid();
    return page.data;
  };
}

// Stable across renders. Fetch is resolved when the browser actually reads.
export const publicCatalogReader:PublicCatalogReader=createPublicCatalogReader((input,init)=>fetch(input,init));
